import { createContext } from 'react';
import { observable, action, decorate, computed } from 'mobx';
import { create, persist } from 'mobx-persist';
import get from 'lodash/get';
import query from 'query-string';
import { t } from '@lingui/macro';

import { reportError } from 'errorReporting';

import {
    authStore,
    userStore,
    catalogStore,
    leanplumStore,
    localesStore,
    familyStore,
} from 'stores';
import { leanplumNotFound } from 'stores/leanplum';
import { UserAgent } from 'agents';
import * as help from 'helpers';
import dayjs from 'services/dayjs';
import { setCustomerUserId } from 'tracking/appsFlyer';

class CommonStore {
    /** @var {bool} */
    isAppReady = false;

    /** @var {bool}
     *
     *  Whether user logged in through a browser, or came here via our desktop/mobile apps
     **/
    isUserComingFromApp = false;

    /** @var {bool} */
    showNavigation = true;

    /** @var {object} */
    routeHistory = null;

    /** @var {object} */
    get routeLocation() {
        return this.routeHistory?.location || {};
    }

    /** @var {bool} */
    loadingUserData = false;

    /** @var {string} */
    appLoadingMessage = null;

    /** @var {bool} */
    hasRequestedPrivacyDownload = null;

    /** @var {array} */
    userGifts = null;

    /** @var {object} */
    userPolicies = null;

    /** @var {object} */
    userLocation = null;

    /** @var {object} */
    userFamily = null;

    /** @var {string} */
    authorizationToken = null;

    /** @var {object} */
    user = null;

    /** @var {object} */
    subscription = null;

    /** @var {object} */
    flashModalProps = {};

    /** @var {object} */
    flashAlertProps = {};

    setApplicationReady() {
        this.isAppReady = true;
        this.appLoadingMessage = null;
    }

    setAppLoadingMessage(message) {
        this.appLoadingMessage = message;
    }

    setApplicationLoading() {
        this.isAppReady = false;
    }

    setNavigation(status) {
        this.showNavigation = status;
    }

    setLoadingUserData(status) {
        this.loadingUserData = status;
    }

    setAuthorizationToken(token) {
        this.authorizationToken = token;
    }

    getFlashAlert(key) {
        const props = this.flashAlertProps[key] || null;
        this.flashAlertProps[key] = null;

        return props;
    }

    get ZIPCodeIsNeeded() {
        const countriesThatNeedZIPCode = ['US', 'GB', 'CA'];
        return countriesThatNeedZIPCode.includes(
            this.user && this.user.country,
        );
    }

    // the user has US ip
    get isFromUSA() {
        const countriesThatNeedZIPCode = ['US'];
        return countriesThatNeedZIPCode.includes(
            this.user && this.user.country,
        );
    }

    getFlashModal(key) {
        const props = this.flashModalProps[key] || null;
        this.flashModalProps[key] = null;

        return props;
    }

    flashAlert(key, props) {
        this.flashAlertProps[key] = props;
    }

    flashModal(key, props) {
        this.flashModalProps[key] = props;
    }

    setUserLocation(location) {
        this.userLocation = location ? location : null;
    }

    setHasRequestedPrivacyDownload(hasRequestedPrivacyDownload) {
        this.hasRequestedPrivacyDownload = hasRequestedPrivacyDownload;
    }

    setUserGifts(gifts) {
        this.userGifts = gifts;
    }

    setUserFamily(family) {
        this.userFamily = family;
    }

    setUserPolicies(policies) {
        this.userPolicies = policies;
    }

    setAuthenticatedUser(user) {
        if (!this.user && user && user.id) {
            setCustomerUserId(user.id);
        }
        const newUser = user ? userStore.decorator(user) : user;
        if (this.user === null || newUser === null) {
            this.user = newUser;
        } else {
            // assign each key to the current user to prevent unnecessary rerender
            Object.keys(this.user).forEach((key) => {
                if (!newUser.hasOwnProperty(key)) {
                    delete this.user[key];
                }
            });
            Object.assign(this.user, newUser);
        }
        if (user && !leanplumNotFound()) {
            return new Promise((resolve) => {
                // we need to wait 600ms to make sure window variables get
                // updates correctly. I tried using the recommended
                // approach (Leanplum.setUserId) but that
                // doesn't work at all.
                !leanplumNotFound() &&
                    window.Leanplum.start(user.id, () =>
                        setTimeout(resolve, 600),
                    );
            });
        }
    }

    // shape the subscription object (e.g. when it comes from the api) to the form that frontend needs
    setSubscription(source) {
        if (!source) {
            this.subscription = null;
            return;
        }

        try {
            const {
                policies,
                gateway,
                // this is deprecated instead use free_trial_info.free_trial_state
                free_trial_state,
                free_trial_info,
                fixed,
                current_plan,
                given_by_admin,
                current_plan: {
                    core_plan_data,
                    core_plan_data: {
                        features,
                        billing_period,
                        subscription_period,
                    },
                },
            } = source;

            const gatewayName = help.plan_sanitize_gateway(gateway);

            const licensed = core_plan_data.features.licensed_content;
            const planDetails = current_plan[gatewayName];
            const details = source[gatewayName + '_details'];

            const plan = help.plan_decorate(current_plan);

            let name = help.plan_get_name(
                current_plan,
                commonStore.userPolicies,
                true,
            );

            const datesFormat = 'll';

            const instrument = features.all_instruments
                ? t`All instruments`
                : !features.instrument
                ? t`(no instrument selected)`
                : help.str_ucfirst(features.instrument);
            const is_upgradable =
                core_plan_data.is_upgradable && policies.can_upgrade;
            const can_resubscribe =
                core_plan_data.renewable && policies.can_resubscribe;
            const billing_cycle = help.plan_get_billing_cycle(
                subscription_period,
                billing_period,
            );
            const renewable = source.renewable
                ? {
                      ...source.renewable,
                      period_end_date: dayjs(
                          source.renewable.period_end_date,
                      ).format(datesFormat),
                  }
                : null;

            let billing = null;

            if (source.billing) {
                billing = {
                    next: '',
                    next_price: {},
                };

                if (source.billing.next) {
                    billing.next = dayjs(source.billing.next.date).format(
                        datesFormat,
                    );

                    const billingPrice = source.billing.next.price;
                    const billingAmount = (billingPrice.amount / 100).toFixed(
                        2,
                    );
                    const billingCurrency = help.str_convert_currency_to_symbol(
                        billingPrice.currency,
                    );

                    billing.next_price = {
                        amount: billingAmount,
                        currency: billingCurrency,
                        string: `${billingCurrency}${billingAmount}`,
                    };

                    if (source.billing.next.discount) {
                        const billingDiscountPrice =
                            source.billing.next.discount;
                        const billingDiscountAmount = (
                            source.billing.next.price.amount / 100
                        ).toFixed(2);
                        const billingDiscountCurrency = help.str_convert_currency_to_symbol(
                            billingDiscountPrice.currency,
                        );

                        billing.next_discount = {
                            amount: billingDiscountAmount,
                            currency: billingDiscountCurrency,
                            string: `${billingDiscountCurrency}${billingDiscountAmount}`,
                        };
                    }

                    if (
                        source.billing.previous &&
                        source.billing.previous.discount &&
                        !source.billing.next.discount
                    ) {
                        const billingPrevDiscountPrice =
                            source.billing.previous.discount;
                        const billingPrevDiscountAmount = (
                            (billingPrice.amount -
                                billingPrevDiscountPrice.amount) /
                            100
                        ).toFixed(2);
                        const billingPrevDiscountCurrency = help.str_convert_currency_to_symbol(
                            billingPrevDiscountPrice.currency,
                        );

                        billing.prev_discount = {
                            amount: billingPrevDiscountAmount,
                            currency: billingPrevDiscountCurrency,
                            string: `${billingPrevDiscountCurrency}${billingPrevDiscountAmount}`,
                        };
                    }
                }
            }

            const family = help.plan_is_family(current_plan);

            const is_family_member = family && !billing; // family members have a family subscription but no billing details

            const amount = plan.price.string;

            const subscription = {
                _source: source,
                name,
                billing,
                licensed,
                family,
                is_family_member,
                gateway: gatewayName,
                payment:
                    gatewayName === 'adyen' && details
                        ? {
                              ...details,
                              card_image: help.str_to_card_image(
                                  details.card_brand,
                              ),
                              card_expiration: details.card_expiration
                                  ? dayjs(details.card_expiration).format(
                                        'MM/YYYY',
                                    )
                                  : null,
                          }
                        : null,
                fixed: fixed
                    ? {
                          end_date: dayjs(fixed.end_date).format(datesFormat),
                      }
                    : null,
                instrument: instrument === 'Voice' ? t`Singing` : instrument,
                is_upgradable,
                given_by_admin,
                can_resubscribe,
                amount,
                free_trial_state,
                free_trial_info,
                plan,
                features,
                planDetails,
                renewable,
                billing_cycle,
            };

            this.subscription = subscription;
        } catch (error) {
            console.log('YS', error);
            reportError('Setting subscription failed', error);
        }
    }

    resetData() {
        this.setAuthenticatedUser(null);
        this.setAuthorizationToken(null);
        this.setSubscription(null);
    }

    retrieveCurrentUser() {
        return authStore
            .current()
            .then((user) => commonStore.setAuthenticatedUser(user));
    }

    retrieveSubscription() {
        return authStore
            .subscription()
            .then((subscription) => {
                commonStore.setSubscription(subscription);
                if (help.plan_is_family(subscription.current_plan)) {
                    familyStore.retrieveFamily();
                }
            })
            .catch((error) => {
                // ToDo: if error code is 500 we should do something so
                // that users won't be exposed to things they're not
                // supposed to see.
                // console.log('YS sub error', e.response.statusCode)
                if (get(error, 'response.statusCode') !== 404) {
                    reportError('retrieveSubscription ', error);
                }
                if (get(error, 'response.statusCode') === 404) {
                    // If error code is 404 it means that the user has no subscription
                    // meaning that the user is free
                    // In this case the store should be updated to have subscription set to null.
                    commonStore.setSubscription(null);
                }
            });
    }

    retrievePrivacy() {
        return authStore
            .hasRequestedPrivacyDownload()
            .then((hasRequestedDownload) =>
                commonStore.setHasRequestedPrivacyDownload(
                    hasRequestedDownload,
                ),
            );
    }

    retrieveGifts() {
        return UserAgent.gifts().then((gifts) =>
            commonStore.setUserGifts(gifts),
        );
    }

    retrievePolicies() {
        return UserAgent.policies().then((policies) =>
            commonStore.setUserPolicies(policies),
        );
    }

    retrieveLocation() {
        return UserAgent.location().then((location) =>
            commonStore.setUserLocation(location),
        );
    }

    retrieveUserData() {
        if (!commonStore.user) {
            return commonStore
                .retrieveLocation()
                .then(catalogStore.retrieveCatalogs)
                .then(commonStore.retrieveCoupon)
                .then(() => {
                    commonStore.setLoadingUserData(false);
                });
        }

        return commonStore
            .retrievePolicies()
            .then(commonStore.retrieveSubscription)
            .then(commonStore.retrieveLocation)
            .then(commonStore.retrieveGifts)
            .then(commonStore.retrievePrivacy)
            .then(catalogStore.retrieveCatalogs)
            .then(commonStore.retrieveCoupon)
            .then(() => {
                commonStore.setLoadingUserData(false);
            });
    }

    retrieveCoupon() {
        const params = query.parse(commonStore.routeLocation.search);

        if (!params.coupon) {
            return true;
        }

        return catalogStore.retrieveCatalogs(params.coupon);
    }

    async sso() {
        await authStore.zendesk();

        return true;
    }

    isLoggedIn() {
        return authStore.isLoggedIn();
    }

    async tokenLogin() {
        const key = '/tokenauth';
        const { pathname, hash } = window.location;
        const token =
            pathname.indexOf(key) >= 0
                ? pathname.replace(`${key}/`, '')
                : hash.replace(`#${key}/`, '');

        if (!token || (pathname.indexOf(key) < 0 && hash.indexOf(key) < 0)) {
            return Promise.resolve();
        } else {
            // Having a token proves user is coming from our mobile or desktop apss
            this.isUserComingFromApp = true;

            commonStore.setAppLoadingMessage(t`Retrieving your information...`);

            // now user will log in
            return authStore
                .tokenLogin(token)
                .then((res) => {
                    const redirectUrl = help.redirect_legacy_tokens(res);
                    commonStore.routeHistory.replace(redirectUrl);
                })
                .catch((error) => {
                    reportError('tokenLogin panic', error);
                });
        }
    }
}

decorate(CommonStore, {
    isAppReady: observable,
    isUserComingFromApp: observable,
    showNavigation: observable,
    setApplicationReady: action,
    setApplicationLoading: action,
    loadingUserData: observable,
    setLoadingUserData: action,
    setNavigation: action,
    authorizationToken: observable,
    setAuthorizationToken: action,
    user: observable,
    setAuthenticatedUser: action,
    flashModal: action,
    getFlashModal: action,
    flashAlert: action,
    getFlashAlert: action,
    subscription: observable,
    hasRequestedPrivacyDownload: observable,
    userGifts: observable,
    userPolicies: observable,
    setHasRequestedPrivacyDownload: action,
    setUserGifts: action,
    userLocation: observable,
    setUserLocation: action,
    setSubscription: action,
    appLoadingMessage: observable,
    setAppLoadingMessage: action,
    ZIPCodeIsNeeded: computed,
    isFromUSA: computed,
});

const hydrate = create({
    storage: localStorage,
    jsonify: true,
});

export const commonStore = persist({
    authorizationToken: true,
    user: { type: 'object' },
})(new CommonStore());

hydrate('common', commonStore).then(async () => {
    try {
        await commonStore.tokenLogin();
        await leanplumStore.start();
        if (commonStore.user) await commonStore.retrieveCurrentUser();
    } catch (error) {
        reportError(
            'Problem in hydrating commonStore: leanplumStore.start',
            error,
        );
        if (error && error.status === 403) {
            return commonStore.resetData();
        }
    }
    try {
        const { locale } = query.parse(window.location.search);
        await localesStore.setupLocale(commonStore.user, locale);
        // TODO: check if following calls can be parallelized
        await commonStore.sso();
        await commonStore.retrieveUserData();
    } catch (error) {
        console.log('YS', error);
        reportError('Problem in hydrating commonStore', error);
    }
    commonStore.setApplicationReady();
});

export default createContext(commonStore);
